import { useState } from 'react';
import {
  unifiedMonetizationData,
  type MonetizationTimePeriod,
  type UnifiedMonetizationKpi,
  monetizationTimePeriodLabels,
} from '@/mocks/adminMonetizationUnified';

function formatCurrency(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString()}`;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

/* ── KPI Card ── */
function MonetizationKpiCard({ kpi, timePeriod }: { kpi: UnifiedMonetizationKpi; timePeriod: MonetizationTimePeriod }) {
  const period = kpi[timePeriod];
  const trendColors = period.trend === 'up' ? 'text-emerald-600' : period.trend === 'down' ? 'text-red-500' : 'text-foreground-400';
  const trendIcons = period.trend === 'up' ? 'ri-arrow-up-line' : period.trend === 'down' ? 'ri-arrow-down-line' : 'ri-subtract-line';

  const colorMap: Record<string, string> = {
    emerald: 'bg-emerald-50 text-emerald-500',
    violet: 'bg-violet-50 text-violet-500',
    rose: 'bg-rose-50 text-rose-500',
    amber: 'bg-amber-50 text-amber-500',
    indigo: 'bg-indigo-50 text-indigo-500',
    sky: 'bg-sky-50 text-sky-500',
    orange: 'bg-orange-50 text-orange-500',
  };

  const valueStr = kpi.unit === '$' ? formatCurrency(period.value) : kpi.unit === '%' ? `${period.value.toFixed(1)}%` : formatNumber(period.value);

  return (
    <div className="bg-background-50 border border-background-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-foreground-400 text-[10px] font-medium uppercase tracking-wide">{kpi.label}</span>
        <div className={`w-8 h-8 rounded-lg ${colorMap[kpi.color] || 'bg-background-200 text-foreground-500'} flex items-center justify-center`}>
          <i className={`${kpi.icon} text-sm`}></i>
        </div>
      </div>
      <p className="text-foreground-950 text-xl font-black mb-1">{valueStr}</p>
      <div className="flex items-center gap-1">
        <i className={`${trendIcons} ${trendColors} text-[10px]`}></i>
        <span className={`${trendColors} text-[10px] font-semibold`}>{period.trendLabel}</span>
      </div>
    </div>
  );
}

/* ── Tops Section ── */
function MonetizationTopsSection({ activeTab, onTabChange }: { activeTab: string; onTabChange: (t: string) => void }) {
  const { tops } = unifiedMonetizationData;
  const tabs = [
    { key: 'campanas', label: 'Campañas', icon: 'ri-megaphone-line' },
    { key: 'patrocinadores', label: 'Patrocinadores', icon: 'ri-star-line' },
    { key: 'marketplace', label: 'Marketplace', icon: 'ri-store-2-line' },
  ];

  const currentData = tops[activeTab as keyof typeof tops] || tops.campanas;

  return (
    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
      <div className="px-5 py-4 border-b border-background-200 flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center"><i className="ri-trophy-line text-amber-600 text-base"></i></div>
        <div><h4 className="text-foreground-950 font-bold text-sm">Rankings de rendimiento</h4><p className="text-foreground-400 text-[10px]">Top por categoría en el período seleccionado</p></div>
      </div>
      <div className="px-4 py-3 border-b border-background-200 flex items-center gap-1.5 overflow-x-auto">
        {tabs.map((tab) => (
          <button key={tab.key} onClick={() => onTabChange(tab.key)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${activeTab === tab.key ? 'bg-amber-500 text-white' : 'bg-background-100 text-foreground-500 hover:bg-background-200 hover:text-foreground-700'}`}>
            <i className={`${tab.icon} text-sm`}></i>{tab.label}
          </button>
        ))}
      </div>
      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead><tr className="border-b border-background-200 bg-background-100/50"><th className="text-center py-3 px-4 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider w-12">#</th><th className="text-left py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Nombre</th><th className="text-right py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Valor</th><th className="text-center py-3 px-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Extra</th><th className="text-center py-3 px-4 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider w-16">Tendencia</th></tr></thead>
          <tbody>{currentData.map((item) => (<tr key={item.rank} className="border-b border-background-200 hover:bg-background-50/50 transition-colors"><td className="py-3 px-4 text-center"><span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold ${item.rank <= 3 ? 'bg-amber-100 text-amber-700' : 'bg-background-200 text-foreground-400'}`}>{item.rank}</span></td><td className="py-3 px-3"><div><p className="text-foreground-800 text-xs font-semibold">{item.name}</p><p className="text-foreground-400 text-[10px]">{item.subtitle}</p></div></td><td className="py-3 px-3 text-right"><span className="text-foreground-800 text-xs font-bold">{item.unit === '$' ? formatCurrency(item.value) : formatNumber(item.value)}{item.unit}</span></td><td className="py-3 px-3 text-center"><span className="text-foreground-500 text-[10px]">{item.extraLabel}: <strong className="text-foreground-700">{item.extraValue}</strong></span></td><td className="py-3 px-4 text-center"><i className={`${item.trend === 'up' ? 'ri-arrow-up-line text-emerald-500' : item.trend === 'down' ? 'ri-arrow-down-line text-red-400' : 'ri-subtract-line text-foreground-300'} text-sm`}></i></td></tr>))}</tbody>
        </table>
      </div>
      <div className="lg:hidden divide-y divide-background-200">{currentData.map((item) => (<div key={item.rank} className="p-4 flex items-center gap-3"><span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold shrink-0 ${item.rank <= 3 ? 'bg-amber-100 text-amber-700' : 'bg-background-200 text-foreground-400'}`}>{item.rank}</span><div className="min-w-0 flex-1"><p className="text-foreground-800 text-xs font-semibold">{item.name}</p><p className="text-foreground-400 text-[10px]">{item.subtitle}</p></div><div className="text-right shrink-0"><span className="text-foreground-800 text-xs font-bold">{item.unit === '$' ? formatCurrency(item.value) : formatNumber(item.value)}{item.unit}</span><p className="text-foreground-400 text-[9px]">{item.extraLabel}: {item.extraValue}</p></div></div>))}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   REVENUE ANALYTICS TAB CONTENT
   ═══════════════════════════════════════════════════════════════ */
export default function RevenueAnalyticsTabContent() {
  const [timePeriod, setTimePeriod] = useState<MonetizationTimePeriod>('mes');
  const [activeTab, setActiveTab] = useState<string>('campanas');

  const { revenueKpis, performanceKpis } = unifiedMonetizationData;
  const totalRevenue = revenueKpis.reduce((sum, kpi) => sum + kpi[timePeriod].value, 0);
  const presetLabel = monetizationTimePeriodLabels[timePeriod];

  return (
    <div className="space-y-5">
      {/* Hero + Time Selector */}
      <div className="bg-background-50 rounded-2xl border border-background-200 p-5 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-amber-100 flex items-center justify-center shrink-0"><i className="ri-money-dollar-circle-line text-xl text-amber-500"></i></div>
            <div>
              <h3 className="text-foreground-950 font-extrabold text-lg">Monetización de tu Radio</h3>
              <p className="text-foreground-400 text-xs mt-0.5">
                Ingresos totales del período <strong className="text-foreground-700">{presetLabel.toLowerCase()}</strong>: <span className="text-amber-600 font-bold">${totalRevenue.toLocaleString()}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center bg-background-100 rounded-full p-1 shrink-0">
            {(Object.entries(monetizationTimePeriodLabels) as [MonetizationTimePeriod, string][]).map(([key, label]) => (
              <button key={key} onClick={() => setTimePeriod(key)} className={`px-4 py-2 rounded-full text-sm font-semibold cursor-pointer transition-all whitespace-nowrap ${timePeriod === key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-400 hover:text-foreground-600'}`}>{label}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Revenue KPI Cards */}
      <div>
        <div className="flex items-center gap-2 mb-3"><div className="w-6 h-6 rounded-md bg-emerald-50 flex items-center justify-center"><i className="ri-hand-coin-line text-emerald-500 text-xs"></i></div><h4 className="text-foreground-800 text-xs font-bold uppercase tracking-wide">Ingresos por Canal</h4></div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {revenueKpis.map((kpi) => (<MonetizationKpiCard key={kpi.id} kpi={kpi} timePeriod={timePeriod} />))}
        </div>
      </div>

      {/* Performance KPI Cards */}
      <div>
        <div className="flex items-center gap-2 mb-3"><div className="w-6 h-6 rounded-md bg-sky-50 flex items-center justify-center"><i className="ri-line-chart-line text-sky-500 text-xs"></i></div><h4 className="text-foreground-800 text-xs font-bold uppercase tracking-wide">Rendimiento Publicitario</h4></div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {performanceKpis.map((kpi) => (<MonetizationKpiCard key={kpi.id} kpi={kpi} timePeriod={timePeriod} />))}
        </div>
      </div>

      {/* Revenue Summary Bar */}
      <div className="bg-background-50 rounded-xl border border-background-200 p-5">
        <div className="flex items-center gap-2.5 mb-4"><div className="w-7 h-7 rounded-lg bg-indigo-50 flex items-center justify-center"><i className="ri-funds-line text-indigo-500 text-sm"></i></div><div><h4 className="text-foreground-950 font-bold text-sm">Distribución de Ingresos</h4><p className="text-foreground-400 text-[10px]">Cómo se reparte tu revenue en {presetLabel.toLowerCase()}</p></div></div>
        <div className="flex h-6 rounded-full overflow-hidden bg-background-100">
          {revenueKpis.map((kpi, idx) => {
            const pct = totalRevenue > 0 ? Math.round((kpi[timePeriod].value / totalRevenue) * 100) : 0;
            const barColors = ['bg-emerald-500', 'bg-violet-500', 'bg-rose-500', 'bg-indigo-500'];
            return <div key={kpi.id} className={`${barColors[idx]} h-full transition-all duration-700 ease-out`} style={{ width: `${pct}%`, minWidth: pct > 0 ? '2px' : '0px' }} title={`${kpi.label}: ${pct}%`}></div>;
          })}
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3">
          {revenueKpis.map((kpi, idx) => {
            const pct = totalRevenue > 0 ? Math.round((kpi[timePeriod].value / totalRevenue) * 100) : 0;
            const barColors = ['bg-emerald-500', 'bg-violet-500', 'bg-rose-500', 'bg-indigo-500'];
            return <div key={kpi.id} className="flex items-center gap-1.5"><span className={`w-2.5 h-2.5 rounded-sm ${barColors[idx]} shrink-0`}></span><span className="text-foreground-500 text-[10px]">{kpi.label}</span><span className="text-foreground-700 text-[10px] font-semibold">{pct}%</span></div>;
          })}
        </div>
      </div>

      {/* Tops Section */}
      <MonetizationTopsSection activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Highlight cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="bg-background-50 rounded-xl border border-background-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0"><i className="ri-hand-coin-line text-emerald-500 text-lg"></i></div>
          <div><p className="text-foreground-400 text-[10px] font-medium">Mayor fuente</p><p className="text-foreground-950 text-sm font-bold">Ingresos Propios</p><p className="text-foreground-400 text-[9px]">Publicidad directa + patrocinios</p></div>
        </div>
        <div className="bg-background-50 rounded-xl border border-background-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0"><i className="ri-megaphone-line text-amber-500 text-lg"></i></div>
          <div><p className="text-foreground-400 text-[10px] font-medium">Revenue Share RFW</p><p className="text-foreground-950 text-sm font-bold">70/30 Radio/RFW</p><p className="text-foreground-400 text-[9px]">Único porcentaje en toda la red</p></div>
        </div>
        <div className="bg-background-50 rounded-xl border border-background-200 p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center shrink-0"><i className="ri-mouse-line text-sky-500 text-lg"></i></div>
          <div><p className="text-foreground-400 text-[10px] font-medium">CTR promedio</p><p className="text-foreground-950 text-sm font-bold">{performanceKpis[0][timePeriod].value}%</p><p className="text-foreground-400 text-[9px]">Click-through rate del período</p></div>
        </div>
      </div>

      {/* Footer badge */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0"><i className="ri-money-dollar-circle-line text-amber-500 text-lg"></i></div>
            <div><p className="text-amber-800 text-xs font-semibold">Panel de Monetización</p><p className="text-amber-600/80 text-[10px]">Métricas de revenue y rendimiento publicitario. Datos unificados sin doble conteo.</p></div>
          </div>
          <span className="px-2.5 py-1 rounded-full bg-amber-200 text-amber-700 text-[10px] font-medium whitespace-nowrap flex items-center gap-1 w-fit"><i className="ri-time-line text-[9px]"></i>Actualizado ahora</span>
        </div>
      </div>
    </div>
  );
}